/**
 * TestCaseCommentsTab — flat comment thread for a test case.
 *
 * Features:
 * - Fetches GET /api/test-cases/:id/comments
 * - Post new comment via POST (Enter in composer, or button)
 * - Edit own comment inline (PUT)
 * - Delete own comment or any comment for admins (DELETE)
 * - Paste image → converts to data-URL and embeds as markdown ![image](...)
 * - Shows max 3 comments by default; "Show all N" to expand
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, Loader2, Pencil, Trash2, Check, X, Image } from 'lucide-react';
import type { ChangeEvent } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/api';

const API_URL = API_BASE_URL;
const DEFAULT_SHOW = 3;

interface IComment {
    _id:         string;
    authorId:    string;
    authorName:  string;
    body:        string;
    createdAt:   string;
    updatedAt?:  string;
    isEdited?:   boolean;
}

interface TestCaseCommentsTabProps {
    testCaseId: string | null;   // null when in 'create new' mode
}

// Formats a relative timestamp: "just now", "5m ago", "2h ago", "Mar 3"
function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins  = Math.floor(diff / 60_000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Renders body: plain text, with inline images rendered as <img>
function CommentBody({ body }: { body: string }) {
    // Replace markdown image syntax ![...](data:... or http...) with <img>
    const parts = body.split(/(!\[.*?\]\([^)]+\))/g);
    return (
        <div className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">
            {parts.map((part, i) => {
                const match = part.match(/^!\[.*?\]\(([^)]+)\)$/);
                if (match) {
                    // Relative paths (e.g. /uploads/uuid.png) are stored in the DB and
                    // must be resolved against the API origin, not the Vite dev server.
                    const src = match[1];
                    const resolvedSrc = src.startsWith('/') ? `${API_URL}${src}` : src;
                    return (
                        <img
                            key={i}
                            src={resolvedSrc}
                            alt="attachment"
                            className="mt-1 max-w-full rounded-lg border border-slate-200 dark:border-slate-700 max-h-48 object-contain"
                        />
                    );
                }
                return <span key={i}>{part}</span>;
            })}
        </div>
    );
}

export function TestCaseCommentsTab({ testCaseId }: TestCaseCommentsTabProps) {
    const { token, user } = useAuth();

    const [comments,    setComments]    = useState<IComment[]>([]);
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState<string | null>(null);
    const [showAll,     setShowAll]     = useState(false);

    // Composer state
    const [body,        setBody]        = useState('');
    const [posting,     setPosting]     = useState(false);
    const [postError,   setPostError]   = useState<string | null>(null);

    // Edit state
    const [editingId,   setEditingId]   = useState<string | null>(null);
    const [editBody,    setEditBody]    = useState('');
    const [editSaving,  setEditSaving]  = useState(false);

    // Delete state
    const [deletingId,  setDeletingId]  = useState<string | null>(null);

    const composerRef   = useRef<HTMLTextAreaElement>(null);
    const fileInputRef  = useRef<HTMLInputElement>(null);

    // ── Fetch ────────────────────────────────────────────────────────────────
    const fetchComments = useCallback(async () => {
        if (!testCaseId || !token) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/test-cases/${testCaseId}/comments`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (data.success && Array.isArray(data.data?.comments)) {
                setComments(data.data.comments as IComment[]);
            }
        } catch {
            setError('Failed to load comments.');
        } finally {
            setLoading(false);
        }
    }, [testCaseId, token]);

    useEffect(() => { fetchComments(); }, [fetchComments]);

    const [uploading, setUploading] = useState(false);

    // ── Shared helper: upload a File to /api/upload and embed the returned URL ──
    async function embedImageFile(file: File) {
        setUploading(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const { data } = await axios.post(
                `${API_URL}/api/upload`,
                form,
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
            );
            if (!data.success || !data.data?.url) {
                setPostError('Image upload failed.');
                return;
            }
            const markdown = `![image](${data.data.url})`;
            setBody((prev) => prev + (prev ? '\n' : '') + markdown);
        } catch {
            setPostError('Image upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    }

    // ── Paste image → upload and embed URL ───────────────────────────────────
    function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
        const items = Array.from(e.clipboardData.items);
        const imageItem = items.find((item) => item.type.startsWith('image/'));
        if (!imageItem) return;
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) embedImageFile(file);
    }

    // ── File picker → upload and embed URL ───────────────────────────────────
    function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;
        embedImageFile(file);
        // Reset so the same file can be selected again
        e.target.value = '';
    }

    // ── Post ─────────────────────────────────────────────────────────────────
    async function handlePost() {
        const trimmed = body.trim();
        if (!trimmed || !testCaseId) return;
        setPosting(true);
        setPostError(null);
        try {
            await axios.post(
                `${API_URL}/api/test-cases/${testCaseId}/comments`,
                { body: trimmed },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setBody('');
            await fetchComments();
            setShowAll(true);
        } catch (err: unknown) {
            const msg = axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : 'Failed to post comment.';
            setPostError(msg);
        } finally {
            setPosting(false);
        }
    }

    // ── Edit ─────────────────────────────────────────────────────────────────
    function startEdit(comment: IComment) {
        setEditingId(comment._id);
        setEditBody(comment.body);
    }

    async function commitEdit() {
        if (!editingId || !editBody.trim() || !testCaseId) return;
        setEditSaving(true);
        try {
            await axios.put(
                `${API_URL}/api/test-cases/${testCaseId}/comments/${editingId}`,
                { body: editBody.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setEditingId(null);
            await fetchComments();
        } catch {
            // keep editor open for retry
        } finally {
            setEditSaving(false);
        }
    }

    // ── Delete ────────────────────────────────────────────────────────────────
    async function handleDelete(commentId: string) {
        if (!testCaseId) return;
        setDeletingId(commentId);
        try {
            await axios.delete(
                `${API_URL}/api/test-cases/${testCaseId}/comments/${commentId}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            await fetchComments();
        } catch {
            // silent
        } finally {
            setDeletingId(null);
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────

    if (!testCaseId) {
        return (
            <div className="flex flex-col items-center justify-center flex-1 gap-2 text-slate-400 p-4">
                <MessageSquare size={22} className="opacity-40" />
                <p className="text-xs text-center">Save this test case first to add comments.</p>
            </div>
        );
    }

    const visibleComments = showAll ? comments : comments.slice(0, DEFAULT_SHOW);
    const hiddenCount     = comments.length - DEFAULT_SHOW;

    return (
        <div data-testid="test-case-comments-tab" className="flex flex-col flex-1 overflow-hidden">
            {/* Comment list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {loading && (
                    <div className="flex justify-center py-4">
                        <Loader2 size={16} className="animate-spin text-slate-400" />
                    </div>
                )}
                {error && (
                    <p className="text-xs text-rose-500 text-center">{error}</p>
                )}
                {!loading && comments.length === 0 && !error && (
                    <div className="flex flex-col items-center justify-center py-8 gap-2 text-slate-400">
                        <MessageSquare size={20} className="opacity-40" />
                        <p className="text-xs">No comments yet. Be the first!</p>
                    </div>
                )}

                {visibleComments.map((comment) => {
                    const isOwn   = comment.authorId === user?.id;
                    const isAdmin = user?.role === 'admin';
                    const canEdit = isOwn;
                    const canDelete = isOwn || isAdmin;
                    const isEditing = editingId === comment._id;
                    const isDeleting = deletingId === comment._id;

                    return (
                        <div key={comment._id} className="group flex gap-2">
                            {/* Avatar initials */}
                            <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {comment.authorName.charAt(0).toUpperCase()}
                            </div>

                            <div className="flex-1 min-w-0">
                                {/* Author + time */}
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                                        {comment.authorName}
                                    </span>
                                    <span className="text-[10px] text-slate-400 flex-shrink-0">
                                        {relativeTime(comment.createdAt)}
                                    </span>
                                    {comment.isEdited && (
                                        <span className="text-[10px] text-slate-400 italic">(edited)</span>
                                    )}
                                </div>

                                {/* Body / edit mode */}
                                {isEditing ? (
                                    <div className="flex flex-col gap-1">
                                        <textarea
                                            autoFocus
                                            value={editBody}
                                            onChange={(e) => setEditBody(e.target.value)}
                                            rows={3}
                                            className="w-full px-2 py-1.5 text-xs border border-blue-400 rounded-lg bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commitEdit();
                                                if (e.key === 'Escape') setEditingId(null);
                                            }}
                                        />
                                        <div className="flex gap-1">
                                            <button
                                                onClick={commitEdit}
                                                disabled={editSaving || !editBody.trim()}
                                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-md disabled:opacity-50 transition-colors"
                                            >
                                                {editSaving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditingId(null)}
                                                className="flex items-center gap-1 px-2 py-1 text-[10px] text-slate-500 hover:text-slate-700 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                <X size={10} /> Cancel
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-slate-400">⌘↵ to save · Esc to cancel</p>
                                    </div>
                                ) : (
                                    <CommentBody body={comment.body} />
                                )}
                            </div>

                            {/* Action buttons — visible on hover */}
                            {!isEditing && (
                                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5">
                                    {canEdit && (
                                        <button
                                            onClick={() => startEdit(comment)}
                                            title="Edit comment"
                                            className="p-1 rounded text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                            <Pencil size={11} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={() => handleDelete(comment._id)}
                                            disabled={isDeleting}
                                            title="Delete comment"
                                            className="p-1 rounded text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                                        >
                                            {isDeleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Show all / collapse toggle */}
                {comments.length > DEFAULT_SHOW && (
                    <button
                        data-testid="comments-show-all-toggle"
                        onClick={() => setShowAll((v) => !v)}
                        className="w-full text-xs text-blue-600 dark:text-blue-400 hover:underline py-1"
                    >
                        {showAll ? 'Show fewer' : `Show all ${comments.length} comments`}
                    </button>
                )}
                {!showAll && hiddenCount > 0 && (
                    <p className="text-[10px] text-slate-400 text-center">
                        {hiddenCount} more comment{hiddenCount !== 1 ? 's' : ''} hidden
                    </p>
                )}
            </div>

            {/* Composer */}
            <div className="border-t border-slate-100 dark:border-slate-800 p-3 flex-shrink-0">
                {postError && (
                    <p className="text-xs text-rose-500 mb-1.5">{postError}</p>
                )}
                <div className="flex gap-2">
                    <textarea
                        ref={composerRef}
                        aria-label="Write a comment"
                        data-testid="comment-composer"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        onPaste={handlePaste}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && body.trim()) {
                                e.preventDefault();
                                handlePost();
                            }
                        }}
                        placeholder="Write a comment… (paste or click 🖼 to attach image)"
                        rows={2}
                        disabled={posting}
                        className="flex-1 px-2.5 py-2 text-xs border border-slate-200 dark:border-gh-border-dark rounded-lg bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />
                    <div className="flex flex-col gap-1">
                        <button
                            data-testid="comment-post-button"
                            aria-label="Post comment"
                            onClick={handlePost}
                            disabled={posting || !body.trim()}
                            title="Post comment (⌘↵)"
                            className="p-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 transition-colors flex-shrink-0"
                        >
                            {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        </button>
                        {/* Image button opens file picker (paste also works) */}
                        <button
                            type="button"
                            data-testid="comment-attach-image-button"
                            aria-label={uploading ? 'Uploading image…' : 'Attach an image'}
                            title={uploading ? 'Uploading…' : 'Attach an image'}
                            disabled={uploading}
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex-shrink-0 disabled:opacity-50"
                        >
                            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Image size={13} />}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileSelect}
                        />
                    </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">⌘↵ to post · Paste or click 🖼 to upload image</p>
            </div>
        </div>
    );
}
